import React, { useState, useEffect } from "react";
import {
  getDoc,
  doc,
  collection,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../../firebase";
import { useNavigate } from "react-router-dom";
import { USStates } from "../../datasource";
import { ReactComponent as DeclareDisaster } from "../../../assets/disaster-icons/declare-disaster.svg";
import {
  FaFolderOpen,
  FaPauseCircle,
  FaCheck,
  FaPlus,
  FaChevronDown,
} from "react-icons/fa";
import {
  DisasterCard,
  ConfirmationModal,
  DeclareWizardModal,
  AddDisasterModal,
} from "./components";

const DisasterDeclaration = ({ className = "" }) => {
  const [data, setData] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [userData, setUserData] = useState("");
  const [centerData, setCenterData] = useState("");
  const [centerState, setCenterState] = useState([]);
  const [centerCounty, setCenterCounty] = useState([]);
  const [canDeclareDisaster, setCanDeclareDisaster] = useState(false);
  const [canApproveDisaster, setCanApproveDisaster] = useState(false);
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [selectedDisaster, setSelectedDisaster] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState(["active"]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isDisasterModalOpen, setIsDisasterModalOpen] = useState(false);
  const [isWizardModalOpen, setIsWizardModalOpen] = useState(false);

  const statuses = ["active", "inactive", "closed", "declared", "approved"];
  const employeeDetails = JSON.parse(sessionStorage.getItem("employeeDetails"));

  const statusColors = {
    active: "#0A8525",
    inactive: "#898989",
    closed: "#CE2222",
    declared: "#DEAA38",
    approved: "#22B0ED",
  };

  const disasterStageStyles = {
    Response: { color: "#FFF", background: "#FC6663" },
    Recovery: { color: "#FFF", background: "#3DE050" },
    Relief: { color: "#FFF", background: "#2C99F8" },
  };

  const selectedUserId = sessionStorage.getItem("userId");

  const getFullStateName = (abbreviation) => {
    const stateObj = USStates.find(
      (state) => state.abbreviation === abbreviation
    );
    return stateObj ? stateObj.name : abbreviation;
  };

  const fetchEmployeeDetails = async (centerId, employeeId) => {
    try {
      const employeeDocRef = doc(
        db,
        "centers",
        centerId,
        "employees",
        employeeId
      );
      const employeeDoc = await getDoc(employeeDocRef);

      if (!employeeDoc.exists()) {
        console.warn("Employee document not found");
        return null;
      }

      return employeeDoc.data();
    } catch (error) {
      console.error("Error fetching employee details:", error);
      return null;
    }
  };

  const updateUserState = (userData, employeeData) => {
    setUserRole(userData.user_role);
    setUserData(userData);
    setCanDeclareDisaster(employeeData?.can_declare_disaster || false);
    setCanApproveDisaster(employeeData?.can_approve_disaster || false);
    setUserName(`${employeeData?.first_name} ${employeeData?.last_name}`);
  };

  useEffect(() => {
    const fetchUserRole = async () => {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user || !selectedUserId) return;

      try {
        const userDoc = await getDoc(doc(db, "users", selectedUserId));
        if (!userDoc.exists()) return;

        const userData = userDoc.data();
        const employeeId = employeeDetails?.id || selectedUserId;

        const employeeData = await fetchEmployeeDetails(
          userData.center_id,
          employeeId
        );

        updateUserState(userData, employeeData);
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    };

    fetchUserRole();
  }, [selectedUserId, employeeDetails]);

  useEffect(() => {
    const fetchCenterData = async () => {
      if (!userData.center_id) return;

      try {
        const centerDocRef = doc(db, "centers", userData.center_id);
        const centerDoc = await getDoc(centerDocRef);

        if (centerDoc.exists()) {
          const centerData = centerDoc.data();
          setCenterData(centerData);

          const fullStateName = getFullStateName(centerData.state);

          setCenterState(fullStateName ? [fullStateName] : []);
          setCenterCounty(centerData.county ? [centerData.county] : []);
        } else {
          console.log("Center document not found");
        }
      } catch (error) {
        console.error("Error fetching center data:", error);
      }
    };

    fetchCenterData();
  }, [userData.center_id]);

  useEffect(() => {
    const fetchData = async () => {
      if (
        !userData ||
        !userData.center_id ||
        !centerCounty ||
        centerCounty.length === 0
      )
        return;

      try {
        let q;
        if (centerCounty && centerCounty.length > 0) {
          q = query(
            collection(db, "declared-disaster"),
            where("center_id", "==", userData.center_id),
            where("affected_counties", "array-contains-any", centerCounty)
          );
        } else {
          // Fallback query without the array-contains-any filter
          q = query(
            collection(db, "declared-disaster"),
            where("center_id", "==", userData.center_id)
          );
        }

        const querySnapshot = await getDocs(q);
        const fetchedData = querySnapshot.docs
          .map((doc) => {
            const data = doc.data();
            const formatDate = (date) =>
              date
                ? new Date(date.seconds * 1000).toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })
                : null;

            return {
              ...data,
              disaster_id: doc.id,
              date_declared: formatDate(data.date_declared),
              approved_date: formatDate(data.approved_date),
            };
          })
          .filter((item) => item.is_deleted !== true);

        setData(fetchedData);
        setFilteredData(
          fetchedData.filter((item) => item.disaster_status === "active")
        );
      } catch (error) {
        console.error("Error fetching disasters:", error);
      }
    };

    fetchData();
  }, [userData, centerCounty]);

  useEffect(() => {
    setFilteredData(
      data.filter((disaster) =>
        selectedStatuses.includes(disaster.disaster_status)
      )
    );
  }, [data, selectedStatuses]);

  const handleStatusChange = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const generateUniqueID = () => {
    return (
      Math.random().toString(36).substr(2, 10).toUpperCase() +
      Math.random().toString(36).substr(2, 10).toLowerCase()
    );
  };

  const handleDeclareDisasterClick = () => setIsConfirmationOpen(true);

  const handleConfirm = (choice) => {
    setIsConfirmationOpen(false);
    if (choice === "no") {
      setIsDisasterModalOpen(true);
    } else {
      setIsWizardModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsDisasterModalOpen(false);
    setIsWizardModalOpen(false);
  };

  const handleDisasterSubmit = async (disasterData) => {
    try {
      const disasterId = generateUniqueID();

      if (!userData?.center_id) {
        console.error("No center_id available");
        return;
      }

      // Format the dates for Firebase
      const formattedData = {
        ...disasterData,
        date_declared: new Date(),
        disaster_id: disasterId,
        center_id: userData.center_id,
        declared_by: disasterData.declared_by || userName,
      };
      console.log("Declared disaster", formattedData);

      if (!formattedData.center_id) {
        console.error("Missing center_id in disaster data");
        return;
      }

      if (!formattedData.declared_by) {
        console.error("Missing declared_by in disaster data");
        return;
      }

      await setDoc(doc(db, "declared-disaster", disasterId), formattedData);

      // Format dates for display before adding to state
      const displayData = {
        ...formattedData,
        date_declared: new Date().toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        }),
      };

      setData((prevData) => [...prevData, displayData]);
    } catch (error) {
      console.error("Error declaring disaster:", error);
    }
  };

  return (
    <div className="h-[calc(100vh-64px)] w-full p-2 !font-inter">
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        onConfirm={handleConfirm}
        canDeclareDisaster={canDeclareDisaster}
      />

      <AddDisasterModal
        isOpen={isDisasterModalOpen}
        onClose={handleModalClose}
        onSubmit={handleDisasterSubmit}
        centerState={centerState}
        centerCounty={centerCounty}
        userData={userData}
        userName={userName}
      />

      <DeclareWizardModal
        isOpen={isWizardModalOpen}
        onClose={handleModalClose}
        onSubmit={handleDisasterSubmit}
        centerState={centerState}
        centerCounty={centerCounty}
        userData={userData}
        userName={userName}
      />
      <div className="disaster-header-label mb-2 mt-2 flex">
        <label>Disaster Management</label>
      </div>
      <div className="mb-2 flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              className="text-gray-700 hover:bg-gray-50 flex min-w-[200px] items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm focus:outline-none"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <span>List of Status</span>
              <FaChevronDown
                className={`text-xs transition-transform ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
                style={{ color: statusColors[selectedStatuses[0]] }}
              />
            </button>
            {dropdownOpen && (
              <div className="absolute mt-1 w-full rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                {statuses.map((status) => (
                  <label
                    key={status}
                    className="hover:bg-gray-50 flex cursor-pointer items-center px-2 py-1"
                    style={{ color: statusColors[status] }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status)}
                      onChange={() => handleStatusChange(status)}
                      className="mr-2 h-4 w-4 rounded border-gray-300"
                    />
                    <span className="text-sm font-bold capitalize">
                      {status}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          className="flex items-center gap-2 rounded-md bg-[#467BA4] px-4 py-3 text-[#F9B54C] transition-colors hover:bg-[#264573]"
          onClick={handleDeclareDisasterClick}
        >
          <FaPlus className="text-sm" />
          <span className="text-sm font-medium">Add Disaster</span>
        </button>
      </div>
      {/* Active Disasters */}
      {filteredData.length === 0 ? (
        <div className="text-center text-gray-500">
          No disasters found for the selected statuses.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {filteredData.map((disaster) => (
            <DisasterCard
              key={disaster.disaster_id}
              disaster={disaster}
              statusColors={statusColors}
              disasterStageStyles={disasterStageStyles}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DisasterDeclaration;
